// Package imports:
import React, { useState, useEffect, useRef } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch } from '@fortawesome/pro-light-svg-icons';
import { useNavigate } from 'react-router-dom';
// Style imports:
// import './AutoComplete.scss';
// Component imports:
import Form from 'react-bootstrap/Form';
// Service imports:
import { turnDateToSimpleTime, isReqError } from '../../services/utils';
// Action imports:
import { requestAutoCompleteItems } from '../../redux-store/actions/dashboardActions';
// Type imports:
import { RootState } from '../../redux-store/reducers';
import { IAutoCompleteItem } from '../../types/api/DashboardTypes';

interface IOwnProps {
    symbol?: string
}
type ReduxProps = ConnectedProps<typeof connector>
type Props = IOwnProps & ReduxProps;

const AutoComplete: React.FC<Props> = ({
    symbol,
    accessTokenV2,
    currentTradingDay,
    autoComplete,
    requestAutoCompleteItems
}) => {
    const [ chosenAutocompleteItem, setChosenAutocompleteItem ] = useState<IAutoCompleteItem | null>(null);
    const formControlRef = useRef<HTMLInputElement | null>(null);
    const navigate = useNavigate();
    
    useEffect(() => {
        if (autoComplete === undefined && autoComplete !== 'waiting') {
            requestAutoCompleteItems(accessTokenV2);
        }
    }, [ autoComplete, requestAutoCompleteItems, accessTokenV2 ]);

    useEffect(() => {
        if (autoComplete === undefined || autoComplete === 'waiting' || isReqError(autoComplete)) {
            return;
        }
        if (isReqError(autoComplete)) {
            console.error('Error fetching autoComplete items:', autoComplete);
            return;
        }

        // Always use default arrays to prevent undefined errors
        const shares = autoComplete.shares || [];
        const bonds = autoComplete.bonds || [];
        const derivatives = autoComplete.derivatives || [];
        const etf = autoComplete.etf || [];

        // Helper to escape regex special characters
        function escapeRegExp(string: string) {
            return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        }

        const options = {
            data: {
                shares,
                bonds,
                derivatives,
                etf
            },
            categories: [
                {
                    listLocation: 'shares',
                    header: 'Shares'
                }, {
                    listLocation: 'bonds',
                    header: 'Bonds'
                }, {
                    listLocation: 'derivatives',
                    header: 'Derivatives'
                }, {
                    listLocation: 'etf',
                    header: 'ETFs'
                }
            ],
            getValue: function(item: IAutoCompleteItem) {
                return item.name + ' (' + item.symbol + ')';
            },
            list: {
                match: {
                    enabled: true,
                    method: function(element: string, phrase: string) {
                        const safePhrase = escapeRegExp(phrase);
                        return element.search(new RegExp(safePhrase, 'i')) > -1;
                    }
                },
                onChooseEvent: () => {
                    // @ts-ignore
                    const selectedItem: IAutoCompleteItem = $('#symbolselect').getSelectedItemData();
                    if (selectedItem) {
                        setChosenAutocompleteItem(selectedItem);
                        navigate(`/sigti/${selectedItem.symbol}?tradingDay=${turnDateToSimpleTime(new Date(currentTradingDay))}`);
                    }
                }
            },
            template: {
                type: 'custom',
                method: function(value: string, item: IAutoCompleteItem) {
                    return `<a href='/sigti/${item.symbol}?tradingDay=${turnDateToSimpleTime(new Date(currentTradingDay))}'>${item.name} (${item.symbol})</a>`;
                }
            },
        };
        // @ts-ignore
        $('#symbolselect').easyAutocomplete(options);
    }, [ autoComplete, currentTradingDay, accessTokenV2 ]);

    return (
        <div id='AutoComplete'>
            <Form.Control
                id='symbolselect'
                placeholder={symbol || 'Search symbol...'}
                ref={formControlRef}
            />
            <FontAwesomeIcon
                className='SearchIcon'
                icon={faSearch}
                onClick={() => formControlRef.current?.focus()}
            />
        </div>
    )
}

function mapStateToProps({ loginReducer, dashboardReducer }: RootState) {
    const { accessTokenV2 } = loginReducer;
    const { currentTradingDay, autoComplete } = dashboardReducer;
    return {
        accessTokenV2,
        currentTradingDay,
        autoComplete
    }
}

const connector = connect(mapStateToProps, { requestAutoCompleteItems });
export default connector(AutoComplete);